import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { TimeRange } from '@/components/analytics/types';
import type { SerializedDateRange } from './analyticsSlice';
import { analyticsApiService } from '@/services/analyticsApiService';
import type { MetricsResponse } from './types';

function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function computeDateRange(
  timeRange: TimeRange,
  customRange: SerializedDateRange | null,
): { startDate: string; endDate: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (customRange) {
    return {
      startDate: toISODate(new Date(customRange.start)),
      endDate: toISODate(new Date(customRange.end)),
    };
  }

  const endDate = toISODate(today);

  const daysMap: Record<TimeRange, number> = {
    '7 Days': 7,
    '30 Days': 30,
    '90 Days': 90,
  };
  const days = daysMap[timeRange];
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));
  return { startDate: toISODate(start), endDate };
}

export const fetchAnalytics = createAsyncThunk<MetricsResponse, string>(
  'analytics/fetchAnalytics',
  async (pageId, { getState }) => {
    const state = getState() as RootState;
    const { timeRange, customRange } = state.analytics;
    const { startDate, endDate } = computeDateRange(timeRange, customRange);
    return analyticsApiService.getMetrics(pageId, startDate, endDate);
  },
);
